import React, { useState, useEffect, useCallback } from 'react';
import { vehicleAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const FUEL_TYPES = ['petrol', 'diesel', 'electric', 'hybrid', 'cng'];
const COLORS = ['White', 'Black', 'Silver', 'Grey', 'Red', 'Blue', 'Green', 'Yellow', 'Orange', 'Brown', 'Other'];

const emptyForm = {
  make: '', model: '', year: '', licensePlate: '', vin: '',
  color: '', fuelType: 'petrol', mileage: '', notes: '',
};

export default function VehiclesPage() {
  const { isStaff } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vehicleAPI.getAll();
      setVehicles(res.data.data || []);
    } catch { toast.error('Failed to load vehicles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditVehicle(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (v) => {
    setEditVehicle(v);
    setForm({ make: v.make, model: v.model, year: v.year, licensePlate: v.licensePlate,
      vin: v.vin || '', color: v.color || '', fuelType: v.fuelType || 'petrol',
      mileage: v.mileage || '', notes: v.notes || '' });
    setShowModal(true);
  };

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editVehicle) {
        await vehicleAPI.update(editVehicle._id, form);
        toast.success('Vehicle updated');
      } else {
        await vehicleAPI.create(form);
        toast.success('Vehicle added');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this vehicle?')) return;
    try {
      await vehicleAPI.delete(id);
      toast.success('Vehicle removed');
      if (selected?._id === id) setSelected(null);
      load();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = vehicles.filter(v => {
    const q = search.toLowerCase();
    return !q || v.make?.toLowerCase().includes(q) || v.model?.toLowerCase().includes(q)
      || v.licensePlate?.toLowerCase().includes(q) || v.owner?.name?.toLowerCase().includes(q);
  });

  const FUEL_ICON = { petrol: '⛽', diesel: '🛢️', electric: '⚡', hybrid: '🔋', cng: '💨' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>Vehicles</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>{filtered.length} registered vehicles</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Vehicle
        </button>
      </div>

      <div className="search-bar" style={{ marginBottom: 20 }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input placeholder="Search by make, model, plate…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="loading-screen" style={{ height: 300 }}><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty-state">
          <div className="empty-state-icon">🚗</div>
          <div className="empty-state-title">No vehicles found</div>
          <div className="empty-state-text">Register your first vehicle to start booking services.</div>
        </div></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {filtered.map(v => (
            <div key={v._id}
              className={`card vehicle-card ${selected?._id === v._id ? 'selected' : ''}`}
              style={{ padding: 20, cursor: 'pointer', transition: 'all 0.2s' }}
              onClick={() => setSelected(selected?._id === v._id ? null : v)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                    🚗
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{v.make} {v.model}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{v.year}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); openEdit(v); }}>Edit</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={e => { e.stopPropagation(); handleDelete(v._id); }}>Del</button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div className="vehicle-detail">
                  <span className="vehicle-detail-label">Plate</span>
                  <span className="vehicle-detail-value" style={{ fontWeight: 700, letterSpacing: 1 }}>{v.licensePlate}</span>
                </div>
                <div className="vehicle-detail">
                  <span className="vehicle-detail-label">Fuel</span>
                  <span className="vehicle-detail-value">{FUEL_ICON[v.fuelType]} {v.fuelType}</span>
                </div>
                {v.color && (
                  <div className="vehicle-detail">
                    <span className="vehicle-detail-label">Color</span>
                    <span className="vehicle-detail-value">{v.color}</span>
                  </div>
                )}
                {v.mileage && (
                  <div className="vehicle-detail">
                    <span className="vehicle-detail-label">Mileage</span>
                    <span className="vehicle-detail-value">{Number(v.mileage).toLocaleString()} km</span>
                  </div>
                )}
                {isStaff && v.owner && (
                  <div className="vehicle-detail" style={{ gridColumn: '1 / -1' }}>
                    <span className="vehicle-detail-label">Owner</span>
                    <span className="vehicle-detail-value">{v.owner.name} · {v.owner.phone}</span>
                  </div>
                )}
              </div>

              {v.vin && (
                <div style={{ marginTop: 10, padding: '6px 10px', background: 'var(--bg-base)', borderRadius: 6, fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                  VIN: {v.vin}
                </div>
              )}

              {/* Expanded service history */}
              {selected?._id === v._id && v.serviceHistory?.length > 0 && (
                <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Service History</div>
                  {v.serviceHistory.slice(0, 5).map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ textTransform: 'capitalize' }}>{s.serviceType?.replace(/-/g, ' ')}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{new Date(s.date).toLocaleDateString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Make *</label>
                    <input className="form-input" placeholder="e.g. Maruti, Honda" value={form.make} onChange={set('make')} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Model *</label>
                    <input className="form-input" placeholder="e.g. Swift, City" value={form.model} onChange={set('model')} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Year *</label>
                    <input type="number" className="form-input" placeholder="2020" value={form.year} onChange={set('year')} required min="1990" max={new Date().getFullYear() + 1} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">License Plate *</label>
                    <input className="form-input" placeholder="MH 01 AB 1234" value={form.licensePlate} onChange={set('licensePlate')} required style={{ textTransform: 'uppercase' }} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Fuel Type</label>
                    <select className="form-select" value={form.fuelType} onChange={set('fuelType')}>
                      {FUEL_TYPES.map(f => <option key={f} value={f} style={{ textTransform: 'capitalize' }}>{f}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Color</label>
                    <select className="form-select" value={form.color} onChange={set('color')}>
                      <option value="">Select color</option>
                      {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Current Mileage (km)</label>
                    <input type="number" className="form-input" placeholder="0" value={form.mileage} onChange={set('mileage')} min="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">VIN (optional)</label>
                    <input className="form-input" placeholder="17-char VIN" value={form.vin} onChange={set('vin')} maxLength={17} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" placeholder="Any special notes about this vehicle…" value={form.notes} onChange={set('notes')} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editVehicle ? 'Update' : 'Add Vehicle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}